import { useEffect, useState } from "react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import ResourceSection, { formatDateDisplay } from "@/components/ResourceSection";
import EducationSection from "@/components/EducationSection";
import WorkExperienceSection from "@/components/WorkExperienceSection";
import ReferencesSection from "@/components/ReferencesSection";
import SkillsSection from "@/components/SkillsSection";
import CustomSections from "@/components/CustomSections";
import UKEssentialsSection from "@/components/UKEssentialsSection";
import EqualOppsSection from "@/components/EqualOppsSection";
import PersonalDetailsSection from "@/components/PersonalDetailsSection";
import { api } from "@/lib/api";
import { copyText, asMarkdown } from "@/lib/clipboard";
import {
  Sparkle, X, CheckCircle, MarkdownLogo, ArrowsDownUp, GraduationCap, Briefcase, Tag, Question,
  ListChecks, IdentificationCard, Globe, UsersThree, User, MagicWand,
} from "@phosphor-icons/react";

// ----- CV Import (unchanged) -----
function ImportCV({ onImported }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [selected, setSelected] = useState({
    profile: true, work: {}, education: {}, apprenticeships: {},
    skills: {}, references: {},
    certifications: {}, languages: {}, awards: {}, publications: {}, projects: {}, volunteer_work: {},
  });

  const parse = async () => {
    if (!file) return;
    setBusy(true); setSuggestions(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/cv", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      setSuggestions(data);
      const init = {
        profile: true, work: {}, education: {}, apprenticeships: {},
        skills: {}, references: {},
        certifications: {}, languages: {}, awards: {}, publications: {}, projects: {}, volunteer_work: {},
      };
      const keyMap = [
        ["work", "work_experience"], ["education", "education"], ["apprenticeships", "apprenticeships"],
        ["skills", "skills"], ["references", "references"],
        ["certifications", "certifications"], ["languages", "languages"], ["awards", "awards"],
        ["publications", "publications"], ["projects", "projects"], ["volunteer_work", "volunteer_work"],
      ];
      keyMap.forEach(([selKey, dataKey]) => {
        (data[dataKey] || []).forEach((_, i) => (init[selKey][i] = true));
      });
      setSelected(init);
      toast.success(data?.saved_document_id ? "CV parsed & saved to Document Safe — review and confirm" : "CV parsed — review and confirm");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Parse failed");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!suggestions) return;
    setBusy(true);
    try {
      const pick = (selKey, dataKey) =>
        (suggestions[dataKey] || []).filter((_, i) => selected[selKey][i]);
      const payload = {
        personal_info: selected.profile ? suggestions.personal_info : null,
        work_experience: pick("work", "work_experience"),
        education: pick("education", "education"),
        apprenticeships: pick("apprenticeships", "apprenticeships"),
        skills: pick("skills", "skills"),
        references: pick("references", "references"),
        certifications: pick("certifications", "certifications"),
        languages: pick("languages", "languages"),
        awards: pick("awards", "awards"),
        publications: pick("publications", "publications"),
        projects: pick("projects", "projects"),
        volunteer_work: pick("volunteer_work", "volunteer_work"),
      };
      const { data } = await api.post("/import/cv/apply", payload);
      const bits = [
        data.work && `${data.work} work`,
        data.education && `${data.education} edu`,
        data.apprenticeships && `${data.apprenticeships} appr`,
        data.skills && `${data.skills} skills`,
        data.references && `${data.references} refs`,
        data.certifications && `${data.certifications} certs`,
        data.languages && `${data.languages} langs`,
        data.awards && `${data.awards} awards`,
        data.publications && `${data.publications} pubs`,
        data.projects && `${data.projects} projects`,
        data.volunteer_work && `${data.volunteer_work} volunteer`,
      ].filter(Boolean).join(" · ");
      toast.success(`Added ${bits || "0 entries"}`);
      setOpen(false); setSuggestions(null); setFile(null);
      onImported?.();
    } catch { toast.error("Apply failed"); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="open-cv-import"
        className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2.5 flex items-center gap-2 hover:bg-[var(--brand-ink)]">
        <Sparkle size={16} weight="duotone"/> import from CV (PDF or DOCX)
      </button>
    );
  }

  return (
    <div className="card-hard rounded-md p-6 bg-[var(--pastel-yellow)]" data-testid="cv-import-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="label-tiny">/cv import</p>
          <h3 className="font-display text-2xl font-semibold tracking-tight">bootstrap your vault from a PDF or Word doc.</h3>
        </div>
        <button onClick={() => { setOpen(false); setSuggestions(null); setFile(null); }}><X size={20}/></button>
      </div>

      {!suggestions && (
        <div>
          <input type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => setFile(e.target.files[0])}
            data-testid="cv-import-file"
            className="card-hard rounded-md px-3 py-2 bg-white text-sm w-full max-w-md"/>
          <div className="mt-4 flex gap-2">
            <button onClick={parse} disabled={!file || busy} data-testid="cv-import-parse"
              className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2 text-sm disabled:opacity-60 hover:bg-[var(--brand-primary)]">
              {busy ? "parsing… (~20s)" : "parse with AI"}
            </button>
          </div>
          <p className="text-xs text-[var(--brand-muted)] mt-3 font-mono-d">
            text-based PDF or DOCX only · 10MB max · apprenticeships get added to both work + education
          </p>
        </div>
      )}

      {suggestions && (
        <div data-testid="cv-import-review">
          <p className="text-sm text-[var(--brand-muted)] mb-4">tick what you want to add. you can always edit after.</p>

          <details open className="mb-4">
            <summary className="cursor-pointer font-medium label-tiny mb-2">/personal info</summary>
            <label className="flex items-center gap-2 mt-3 text-sm">
              <input type="checkbox" checked={selected.profile} onChange={(e) => setSelected({ ...selected, profile: e.target.checked })} data-testid="cv-sel-profile"/>
              {suggestions.personal_info?.full_name || "(no name found)"} · {suggestions.personal_info?.email || ""} · {suggestions.personal_info?.location || ""}
            </label>
          </details>

          {[
            ["work", "work_experience", "work experience", (x) => `${x.role || "?"} · ${x.employer || "?"} (${formatDateDisplay(x.start_date)}—${x.current ? "now" : formatDateDisplay(x.end_date)})`],
            ["education", "education", "education", (x) => `${x.qualification || "?"} · ${x.provider || "?"} (${x.grade || ""})`],
            ["apprenticeships", "apprenticeships", "apprenticeships (added to both work + edu)", (x) => `${x.role || "?"} @ ${x.employer || "?"} · ${x.qualification || "?"}`],
            ["skills", "skills", "skills", (x) => `${x.name || "?"}${x.level ? ` · ${x.level}` : ""}${x.category ? ` · ${x.category}` : ""}`],
            ["references", "references", "references", (x) => `${x.name || "?"}${x.role ? ` · ${x.role}` : ""}${x.company ? ` @ ${x.company}` : ""}${x.email ? ` · ${x.email}` : ""}`],
            ["certifications", "certifications", "certifications", (x) => `${x.name || "?"}${x.issuer ? ` · ${x.issuer}` : ""}${x.issue_date ? ` · ${x.issue_date}` : ""}`],
            ["languages", "languages", "languages", (x) => `${x.language || "?"}${x.proficiency ? ` · ${x.proficiency}` : ""}`],
            ["awards", "awards", "awards", (x) => `${x.name || "?"}${x.issuer ? ` · ${x.issuer}` : ""}${x.date ? ` · ${x.date}` : ""}`],
            ["publications", "publications", "publications", (x) => `${x.title || "?"}${x.publisher ? ` · ${x.publisher}` : ""}${x.date ? ` · ${x.date}` : ""}`],
            ["projects", "projects", "projects", (x) => `${x.name || "?"}${x.role ? ` · ${x.role}` : ""}${x.start_date ? ` (${x.start_date}${x.end_date ? `—${x.end_date}` : ""})` : ""}`],
            ["volunteer_work", "volunteer_work", "volunteer work", (x) => `${x.role || "?"} @ ${x.organisation || "?"}${x.start_date ? ` (${x.start_date}${x.currently ? "—present" : (x.end_date ? `—${x.end_date}` : "")})` : ""}`],
          ].map(([key, dataKey, label, fmt]) => {
            const list = suggestions[dataKey] || [];
            if (list.length === 0) return null;
            return (
              <details key={key} open className="mb-4">
                <summary className="cursor-pointer font-medium label-tiny mb-2">/{label} ({list.length})</summary>
                <ul className="mt-2 space-y-2">
                  {list.map((x, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <input type="checkbox" className="mt-1"
                        data-testid={`cv-sel-${key}-${i}`}
                        checked={!!selected[key][i]}
                        onChange={(e) => setSelected({ ...selected, [key]: { ...selected[key], [i]: e.target.checked } })}/>
                      <span>{fmt(x)}</span>
                    </li>
                  ))}
                </ul>
              </details>
            );
          })}

          <div className="mt-4 flex gap-2">
            <button onClick={apply} disabled={busy} data-testid="cv-import-apply"
              className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--brand-primary)]">
              <CheckCircle weight="bold" size={16}/> {busy ? "adding…" : "add to vault"}
            </button>
            <button onClick={() => setSuggestions(null)} className="btn-hard bg-white rounded-md px-4 py-2 text-sm">re-upload</button>
          </div>
        </div>
      )}
    </div>
  );
}

const SECTION_LABELS = {
  work: "Work Experience",
  education: "Education",
  references: "References",
  skills: "Skills",
  custom: "Custom Sections",
};

// ----- Import from Transcript / Certificate -----
// Complementary to CV import: uploads a transcript / results slip / GCSE
// certificate → parses via /api/ai/extract-subjects/document → creates a
// fresh Education entry with the extracted subjects + qualification-wide
// fields (classification, final mark, credits, mode of study). Cuts the
// "click into education → scroll → click extract from doc" chain to one
// step, at the same top-level toolbar row as `import from CV`.
function ImportTranscript({ onImported }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [defaultLevel, setDefaultLevel] = useState("Module");
  const [programme, setProgramme] = useState("");
  const [provider, setProvider] = useState("");

  const parseAndSave = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/ai/extract-subjects/document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { default_level: defaultLevel || "Module" },
        timeout: 120000,
      });
      const rows = data?.subjects || [];
      const qual = data?.qualification || {};
      if (rows.length === 0 && !qual.classification && !qual.final_mark) {
        toast.error("Couldn't extract any rows from that document");
        return;
      }
      // Create the new Education entry with all the parsed data
      const entry = {
        qualification: programme || "",
        provider: provider || "",
        location: "",
        start_date: "",
        end_date: "",
        subjects: rows.map((s) => ({
          subject: s.subject || "",
          grade:   s.grade   || "",
          year:    s.year    || "",
          level:   s.level   || defaultLevel || "Module",
          module_code: s.module_code || "",
          mark:        s.mark || "",
          result:      s.result || "",
          credits:     s.credits || "",
          attempt:     s.attempt || "",
        })),
        classification:    qual.classification    || "",
        final_mark:        qual.final_mark        || "",
        credits_achieved:  qual.credits_achieved  || "",
        mode_of_study:     qual.mode_of_study     || "",
      };
      await api.post("/education", entry);
      const savedNote = data?.saved_document_id ? " · saved to Document Safe" : "";
      const ocrNote = data?.ocr_used ? " · OCR'd" : "";
      toast.success(`Added a new education entry with ${rows.length} row${rows.length === 1 ? "" : "s"}${ocrNote}${savedNote}`);
      setOpen(false); setFile(null); setProgramme(""); setProvider("");
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Parse failed");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="open-transcript-import"
        className="btn-hard bg-[var(--pastel-mint)] rounded-md px-4 py-2.5 flex items-center gap-2"
        title="upload an academic transcript, GCSE certificate, or A-Level results slip → we extract every row for you">
        <MagicWand size={16} weight="fill"/> import from transcript
      </button>
    );
  }

  return (
    <div className="card-hard rounded-md p-6 bg-[var(--pastel-mint)] w-full" data-testid="transcript-import-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="label-tiny">/transcript import</p>
          <h3 className="font-display text-2xl font-semibold tracking-tight">import an academic transcript.</h3>
          <p className="text-xs text-[var(--brand-muted)] mt-1">
            uni transcript, GCSE certificate, A-Level results slip — the AI reads the table and creates a new education entry with every row filled in.
          </p>
        </div>
        <button onClick={() => { setOpen(false); setFile(null); setProgramme(""); setProvider(""); }} data-testid="close-transcript-import">
          <X size={20}/>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <label className="block">
          <span className="label-tiny block mb-1">Programme (optional — we&apos;ll leave blank if unsure)</span>
          <input
            data-testid="transcript-programme"
            value={programme}
            onChange={(e) => setProgramme(e.target.value)}
            placeholder="e.g. BSc Politics, A-Levels"
            className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
        </label>
        <label className="block">
          <span className="label-tiny block mb-1">Institution (optional)</span>
          <input
            data-testid="transcript-provider"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            placeholder="e.g. King's College London"
            className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-2">
        <label className="text-xs text-[var(--brand-muted)] inline-flex items-center gap-1.5">
          default level (used when the document doesn&apos;t say)
          <select
            data-testid="transcript-default-level"
            value={defaultLevel}
            onChange={(e) => setDefaultLevel(e.target.value)}
            className="card-hard rounded-md px-2 py-1 bg-white text-xs">
            <option value="GCSE">GCSE</option>
            <option value="A-Level">A-Level</option>
            <option value="AS-Level">AS-Level</option>
            <option value="BTEC">BTEC</option>
            <option value="T-Level">T-Level</option>
            <option value="NVQ">NVQ</option>
            <option value="IB">IB</option>
            <option value="Module">Module (uni)</option>
            <option value="Other">Other</option>
          </select>
        </label>
      </div>

      <input
        type="file"
        accept=".pdf,.docx,.jpg,.jpeg,.png,.webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp"
        onChange={(e) => setFile(e.target.files[0])}
        data-testid="transcript-file"
        className="card-hard rounded-md px-3 py-2 bg-white text-sm w-full max-w-md"/>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={parseAndSave} disabled={!file || busy}
          data-testid="transcript-parse-save"
          className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2 text-sm disabled:opacity-60">
          {busy ? "extracting… (~20s)" : "parse & add to vault"}
        </button>
      </div>

      <p className="text-xs text-[var(--brand-muted)] mt-3 font-mono-d">
        PDF, DOCX, or photo (JPG/PNG/WEBP) · 10MB max · scans and paper certificates are OCR&apos;d automatically
      </p>
    </div>
  );
}

function SectionRenderer({ section }) {
  switch (section) {
    case "work":       return <WorkExperienceSection />;
    case "education":  return <EducationSection />;
    case "references": return <ReferencesSection />;
    case "skills":     return <SkillsSection />;
    case "custom":     return <CustomSections />;
    default: return null;
  }
}

// Application-form chronology — each tab matches what UK recruiters actually ask for, in order
const TABS = [
  { key: "all",          label: "All",            icon: ListChecks,         sections: ["work","education","skills","custom","references"] },
  { key: "personal",     label: "Personal",       icon: User,               kind: "personal" },
  { key: "essentials",   label: "App Essentials", icon: Globe,              kind: "essentials" },
  { key: "education",    label: "Education",      icon: GraduationCap,      sections: ["education"] },
  { key: "work",         label: "Work",           icon: Briefcase,          sections: ["work"] },
  { key: "skills",       label: "Skills",         icon: Tag,                sections: ["skills"] },
  { key: "custom",       label: "Additional",     icon: Question,           sections: ["custom"] },
  { key: "references",   label: "References",     icon: IdentificationCard, sections: ["references"] },
  { key: "equal_opps",   label: "Equal Opps",     icon: UsersThree,         kind: "equal_opps" },
];

export default function Vault() {
  const [order, setOrder] = useState(["work", "education", "references", "skills", "custom"]);
  const [reordering, setReordering] = useState(false);
  const [dragSection, setDragSection] = useState(null);
  const [dragOverSection, setDragOverSection] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    api.get("/profile/sections-order").then(({ data }) => {
      if (Array.isArray(data?.order) && data.order.length) setOrder(data.order);
    }).catch(() => {});
    // mount-only: api is a stable singleton, setOrder is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistOrder = async (next) => {
    setOrder(next);
    try { await api.put("/profile/sections-order", { order: next }); }
    catch { toast.error("Couldn't save order"); }
  };

  const onDragStart = (s) => setDragSection(s);
  const onDragOver = (e, s) => { e.preventDefault(); setDragOverSection(s); };
  const onDrop = (target) => {
    if (!dragSection || dragSection === target) { setDragSection(null); setDragOverSection(null); return; }
    const next = order.slice();
    const fromIdx = next.indexOf(dragSection);
    const toIdx = next.indexOf(target);
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, dragSection);
    setDragSection(null); setDragOverSection(null);
    persistOrder(next);
  };

  const copyMarkdownAll = async () => {
    const { data } = await api.get("/export/profile");
    copyText(asMarkdown.fullVault(data), "Vault copied as Markdown");
  };

  const currentTab = TABS.find((t) => t.key === activeTab) || TABS[0];
  const visibleOrder = currentTab.sections ? order.filter((s) => currentTab.sections.includes(s)) : [];

  return (
    <AppShell title="The Vault">
      <p className="text-[var(--brand-muted)] mb-6 max-w-2xl">
        enter it once. it lives here forever. tabs mirror a typical application form — fill in the order you&apos;ll need it.
      </p>

      {/* Sub-tabs */}
      <div className="card-hard rounded-md bg-white p-1.5 mb-6 overflow-x-auto" data-testid="vault-tabs">
        <div className="flex gap-1 min-w-max">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            return (
              <button key={t.key}
                data-testid={`vault-tab-${t.key}`}
                onClick={() => setActiveTab(t.key)}
                className={`px-3 py-1.5 rounded text-xs flex items-center gap-1.5 whitespace-nowrap ${
                  active ? "bg-[var(--brand-ink)] text-white" : "hover:bg-[var(--brand-bg)]"
                }`}>
                <Icon size={14} weight={active ? "fill" : "regular"}/> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Top toolbar — only show on tabs that render section content */}
      {currentTab.sections && (
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <ImportCV onImported={() => setRefreshKey((k) => k + 1)} />
          {(activeTab === "all" || activeTab === "education") && (
            <ImportTranscript onImported={() => setRefreshKey((k) => k + 1)} />
          )}
          <button onClick={copyMarkdownAll} data-testid="vault-copy-markdown"
            className="btn-hard bg-white rounded-md px-4 py-2.5 flex items-center gap-2">
            <MarkdownLogo size={16} weight="bold"/> copy vault as Markdown
          </button>
          {activeTab === "all" && (
            <button onClick={() => setReordering(!reordering)} data-testid="vault-reorder-toggle"
              className={`btn-hard rounded-md px-4 py-2.5 flex items-center gap-2 ${reordering ? "bg-[var(--brand-ink)] text-white hover:bg-[var(--brand-ink)]" : "bg-white"}`}>
              <ArrowsDownUp size={16} weight="bold"/> {reordering ? "done reordering" : "reorder sections"}
            </button>
          )}
        </div>
      )}

      {/* Special-tab content */}
      {currentTab.kind === "personal" && <PersonalDetailsSection/>}
      {currentTab.kind === "essentials" && <UKEssentialsSection/>}
      {currentTab.kind === "equal_opps" && <EqualOppsSection/>}

      {/* Section-tab content */}
      {currentTab.sections && (
        <div className="space-y-8" key={refreshKey}>
          {visibleOrder.map((s) => (
            <div key={s}
              draggable={reordering && activeTab === "all"}
              onDragStart={() => reordering && activeTab === "all" && onDragStart(s)}
              onDragOver={(e) => reordering && activeTab === "all" && onDragOver(e, s)}
              onDrop={() => reordering && activeTab === "all" && onDrop(s)}
              onDragEnd={() => { setDragSection(null); setDragOverSection(null); }}
              data-testid={`vault-section-${s}`}
              className={`relative transition ${reordering && activeTab === "all" ? "ring-2 ring-dashed ring-[var(--brand-ink)] rounded-md" : ""} ${
                dragOverSection === s && dragSection !== s ? "ring-4 ring-[var(--brand-primary)]" : ""
              } ${dragSection === s ? "opacity-40" : ""}`}>
              {reordering && activeTab === "all" && (
                <div className="absolute -top-3 left-4 bg-[var(--brand-ink)] text-white text-[10px] font-mono-d uppercase tracking-widest px-2 py-0.5 rounded z-10">
                  drag · {SECTION_LABELS[s]}
                </div>
              )}
              <SectionRenderer section={s} />
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
